import gql from 'graphql-tag';
import React from 'react';
import { useMutation, useQuery } from 'react-apollo';
import { RouteComponentProps } from 'react-router';
import { MenuItemInput } from './../../globalTypes';
import { optionGroupFragment } from './fragments';
import { GetMenuItem, GetMenuItemVariables } from './__generated__/GetMenuItem';
import { UpdateMenuItem, UpdateMenuItemVariables } from './__generated__/UpdateMenuItem';
import { MenuItemForm } from './Form';

interface EditMenuItemRouterParams {
  venueId: string;
  menuId: string;
  categoryId: string;
  id: string;
}

type EditMenuItemProps = RouteComponentProps<EditMenuItemRouterParams>;

const GET_MENU_ITEM = gql`
  query GetMenuItem($venueId: ID!, $menuId: ID!, $categoryId: ID!, $itemId: ID!) {
    venue(id: $venueId) {
      features
    }
    venueTaxes(id: $venueId) {
      id
      taxName
      taxPercentage
    }
    menuItem(venueId: $venueId, menuId: $menuId, categoryId: $categoryId, itemId: $itemId) {
      enabled
      featured
      taxable
      price
      item {
        id
        description
        imageUrl
        name
        alcoholServings
        menuCategoryId
        shared
        posInfo {
          externalId
          overridedFields
        }
        taxes {
          id
          taxName
          taxPercentage
        }
      }
      optionGroups {
        ...MenuOptionGroupFields
      }
    }
  }
  ${optionGroupFragment}
`;

const SAVE_MENU_ITEM = gql`
  mutation UpdateMenuItem($menuId: ID!, $categoryId: ID!, $itemId: ID!, $menuItem: MenuItemInput!) {
    updateMenuItem(menuId: $menuId, categoryId: $categoryId, itemId: $itemId, menuItem: $menuItem) {
      item {
        id
      }
    }
  }
`;

export const EditMenuItem: React.FC<EditMenuItemProps> = (props) => {
  const { venueId, menuId, categoryId, id: itemId } = props.match.params;
  const { loading, data, error } = useQuery<GetMenuItem, GetMenuItemVariables>(GET_MENU_ITEM, {
    fetchPolicy: 'no-cache',
    variables: {
      venueId,
      menuId,
      categoryId,
      itemId,
    },
  });

  const [saveMenuItem, saveMenuItemState] = useMutation<UpdateMenuItem, UpdateMenuItemVariables>(SAVE_MENU_ITEM, {
    onCompleted() {
      props.history.push(`/venue/${venueId}/menu/list/${menuId}/category/${categoryId}`);
    },
  });

  const onSubmit = React.useCallback(
    async (data: MenuItemInput) => {
      saveMenuItem({
        variables: {
          menuId,
          categoryId,
          itemId,
          menuItem: data,
        },
      });
    },
    [menuId, categoryId, itemId, saveMenuItem]
  );

  return (
    <MenuItemForm
      venueId={venueId}
      categoryId={categoryId}
      menuId={menuId}
      drinkId={itemId}
      onSubmit={onSubmit}
      loading={loading ?? saveMenuItemState.loading}
      error={error ?? saveMenuItemState.error}
      history={props.history}
      taxes={data?.venueTaxes}
      data={
        data
          ? {
              item: data.menuItem,
              venue: data.venue,
            }
          : undefined
      }
    />
  );
};
